import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  AlertTriangle,
  Activity,
  TrendingUp,
  Package,
  Camera,
  Scan,
} from 'lucide-react';
import { apiClient, DashboardMetrics } from '../api/client';

function MetricCard({
  title,
  value,
  icon: Icon,
  trend,
  color = 'primary',
}: {
  title: string;
  value: number | string;
  icon: React.ElementType;
  trend?: string;
  color?: 'primary' | 'green' | 'red' | 'yellow';
}) {
  const colorClasses = {
    primary: 'bg-primary-100 text-primary-600',
    green: 'bg-green-100 text-green-600',
    red: 'bg-red-100 text-red-600',
    yellow: 'bg-yellow-100 text-yellow-600',
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {trend && <p className="text-xs text-gray-400 mt-1">{trend}</p>}
        </div>
        <div className={`p-3 rounded-full ${colorClasses[color]}`}>
          <Icon size={24} />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchMetrics() {
      try {
        const data = await apiClient.getDashboard();
        setMetrics(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load metrics');
      } finally {
        setLoading(false);
      }
    }

    fetchMetrics();
    const interval = setInterval(fetchMetrics, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="grid grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  if (!metrics) return null;

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
      <p className="text-sm text-gray-500 mb-6">Locate2u app only. FedEx devices are not counted.</p>

      {/* Top metrics */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        <MetricCard
          title="Active Users Now"
          value={metrics.active_users.current}
          icon={Users}
          trend={`${metrics.active_users.today} today`}
          color="green"
        />
        <MetricCard
          title="Daily Active Users"
          value={metrics.active_users.today}
          icon={TrendingUp}
          trend={`${metrics.active_users.week} this week`}
          color="primary"
        />
        <MetricCard
          title="Crashes Today"
          value={metrics.crashes.today}
          icon={AlertTriangle}
          trend={`${metrics.crashes.week} this week`}
          color={metrics.crashes.today > 0 ? 'red' : 'green'}
        />
        <MetricCard
          title="Stops Completed"
          value={metrics.activity.stops_today}
          icon={Package}
          color="green"
        />
      </div>

      {/* Activity metrics */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <MetricCard
          title="Photos Today"
          value={metrics.activity.photos_today}
          icon={Camera}
        />
        <MetricCard
          title="Scans Today"
          value={metrics.activity.scans_today}
          icon={Scan}
        />
        <MetricCard
          title="Monthly Active Users"
          value={metrics.active_users.month}
          icon={Activity}
        />
      </div>

      <div className="grid grid-cols-2 gap-8">
        {/* Top errors */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Top Errors (This Week)</h2>
          {metrics.top_errors.length === 0 ? (
            <p className="text-gray-500 text-sm">No errors this week</p>
          ) : (
            <div className="space-y-3">
              {metrics.top_errors.slice(0, 5).map((error, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {error.error_message}
                    </p>
                    <p className="text-xs text-gray-500">
                      {error.affected_users} users affected
                    </p>
                  </div>
                  <span className="badge badge-error">{error.count}</span>
                </div>
              ))}
            </div>
          )}
          <Link
            to="/crashes"
            className="block mt-4 text-sm text-primary-600 hover:text-primary-700"
          >
            View all crashes →
          </Link>
        </div>

        {/* Version distribution */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Version Distribution</h2>
          {metrics.versions.length === 0 ? (
            <p className="text-gray-500 text-sm">No version data available</p>
          ) : (
            <div className="space-y-3">
              {metrics.versions.slice(0, 6).map((version, index) => (
                <div key={`${version.version}-${version.build_number || index}-${version.platform}`} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span
                      className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                        version.platform === 'ios'
                          ? 'bg-gray-800 text-white'
                          : 'bg-green-600 text-white'
                      }`}
                      title={version.platform === 'ios' ? 'iOS' : 'Android'}
                    >
                      {version.platform === 'ios' ? 'iOS' : 'And'}
                    </span>
                    <span className="text-sm font-medium text-gray-900">
                      v{version.version}
                      {version.build_number && (
                        <span className="text-gray-500 ml-1">({version.build_number})</span>
                      )}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-500">{version.users} users</span>
                    {version.crashes > 0 && (
                      <span className="badge badge-error">{version.crashes} crashes</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Platform breakdown */}
      <div className="card mt-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Platform Crashes (This Week)</h2>
        <div className="flex gap-8">
          {Object.entries(metrics.crashes.by_platform).map(([platform, count]) => (
            <div key={platform} className="flex items-center gap-3">
              <div
                className={`w-4 h-4 rounded ${
                  platform === 'ios' ? 'bg-blue-500' : 'bg-green-500'
                }`}
              />
              <span className="text-sm text-gray-700">
                {platform === 'ios' ? 'iOS' : 'Android'}: {count} crashes
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
